import { useState } from 'react';
import useStore from '../store/useStore';
import midiService from '../services/midiService';
import DeviceTransferService, { buildProfileCommands } from '../services/deviceTransferService';
import { FOOTSWITCH_FUNCTIONS, ENCODER_FUNCTIONS } from '../constants/functions';
import { FiUpload, FiDownload, FiSettings } from 'react-icons/fi';
import './ConfigurationEditor.css';
import { useToast } from './Toast';

/**
 * Configuration Editor Component
 * Configure footswitch assignments, encoder function, and MIDI settings
 */

function ConfigurationEditor() {
    // Selector subscriptions: only re-render on the values this component uses.
    const currentProfile = useStore(state => state.currentProfile);
    const setCurrentProfile = useStore(state => state.setCurrentProfile);
    const setProfileModified = useStore(state => state.setProfileModified);
    const isConnected = useStore(state => state.isConnected);
    const sendNoteOn = useStore(state => state.sendNoteOn);
    const sendNoteOff = useStore(state => state.sendNoteOff);
    const sendControlChange = useStore(state => state.sendControlChange);
    const [showMIDISettings, setShowMIDISettings] = useState(false);
    const [transferResult, setTransferResult] = useState(null);
    const notify = useToast();

    if (!currentProfile) return null;

    // Group functions by category for dropdowns
    const groupedFunctions = FOOTSWITCH_FUNCTIONS.reduce((acc, func) => {
        const cat = func.category || 'Other';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(func);
        return acc;
    }, {});

    const updateFootswitch = (footswitch, field, value) => {
        setCurrentProfile({
            ...currentProfile,
            footswitches: {
                ...currentProfile.footswitches,
                [footswitch]: {
                    ...currentProfile.footswitches[footswitch],
                    [field]: value
                }
            }
        });
        setProfileModified(true);
    };

    const updateEncoder = (value) => {
        setCurrentProfile({
            ...currentProfile,
            encoder: { function: value }
        });
        setProfileModified(true);
    };

    const updateMIDISetting = (field, value) => {
        setCurrentProfile({
            ...currentProfile,
            midiSettings: {
                ...currentProfile.midiSettings,
                [field]: value
            }
        });
        setProfileModified(true);
    };

    const updateTrackSetting = (field, value) => {
        setCurrentProfile({
            ...currentProfile,
            trackSettings: {
                ...currentProfile.trackSettings,
                [field]: value
            }
        });
        setProfileModified(true);
    };

    const handleSendToDevice = async () => {
        setTransferResult(null);

        // Do not pretend to send when there is no connection.
        if (!isConnected) {
            setTransferResult({
                success: false,
                message: 'Not connected. Connect to a MIDI device before sending.'
            });
            setTimeout(() => setTransferResult(null), 5000);
            return;
        }

        // Resolve the profile into an ordered list of pure command descriptors,
        // then actually emit each one via the store so it appears in the MIDI Monitor.
        const channel = currentProfile.midiSettings.channel || 1;
        const commands = buildProfileCommands(currentProfile);
        let sent = 0;

        for (const cmd of commands) {
            const cmdChannel = cmd.channel || channel;
            if (cmd.type === 'note') {
                sendNoteOn(cmd.value, 127, cmdChannel);
                const note = cmd.value;
                setTimeout(() => sendNoteOff(note, cmdChannel), 100);
                sent += 1;
            } else if (cmd.type === 'combo') {
                for (const note of cmd.values) {
                    sendNoteOn(note, 127, cmdChannel);
                    const n = note;
                    setTimeout(() => sendNoteOff(n, cmdChannel), 100);
                }
                sent += 1;
            } else if (cmd.type === 'cc') {
                sendControlChange(cmd.value, 64, cmdChannel);
                sent += 1;
            }
        }

        setTransferResult({
            success: true,
            message: sent > 0
                ? `Sent ${sent} command(s) to device (check the MIDI Monitor).`
                : 'No enabled commands to send for this profile.'
        });

        setTimeout(() => setTransferResult(null), 5000);
    };

    const handleReadFromDevice = () => {
        // Instantiated lazily: the Read button is permanently disabled
        // (hardware limitation), so there is no reason to build this per render.
        const result = new DeviceTransferService(midiService).readFromDevice();
        notify(result.error + '\n\n' + result.note, 'error');
    };

    return (
        <div className="configuration-editor glass">
            <div className="config-header">
                <h3>⚙️ Device Configuration</h3>
                <button
                    className="btn-premium"
                    onClick={() => setShowMIDISettings(!showMIDISettings)}
                >
                    <FiSettings /> {showMIDISettings ? 'Hide' : 'Show'} MIDI Settings
                </button>
            </div>

            {/* Footswitch Assignments */}
            <div className="config-section">
                <h4>Footswitch Assignments</h4>

                {/* Built-in Footswitches */}
                <div className="footswitch-grid">
                    <div className="footswitch-config">
                        <label htmlFor="fs1-function">FS1 (Built-in Left)</label>
                        <select
                            id="fs1-function"
                            value={currentProfile.footswitches.fs1.function}
                            onChange={(e) => updateFootswitch('fs1', 'function', e.target.value)}
                            className="config-select"
                        >
                            {Object.entries(groupedFunctions).map(([category, funcs]) => (
                                <optgroup key={category} label={category}>
                                    {funcs.map(func => (
                                        <option key={func.value} value={func.value}>
                                            {func.label}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                        <label className="hold-function-label" htmlFor="fs1-hold">Hold function</label>
                        <select
                            id="fs1-hold"
                            value={currentProfile.footswitches.fs1.holdFunction || ''}
                            onChange={(e) => updateFootswitch('fs1', 'holdFunction', e.target.value || null)}
                            className="config-select"
                        >
                            <option value="">None</option>
                            {Object.entries(groupedFunctions).map(([category, funcs]) => (
                                <optgroup key={category} label={category}>
                                    {funcs.map(func => (
                                        <option key={func.value} value={func.value}>
                                            {func.label}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>

                    </div>

                    <div className="footswitch-config">
                        <label htmlFor="fs2-function">FS2 (Built-in Right)</label>
                        <select
                            id="fs2-function"
                            value={currentProfile.footswitches.fs2.function}
                            onChange={(e) => updateFootswitch('fs2', 'function', e.target.value)}
                            className="config-select"
                        >
                            {Object.entries(groupedFunctions).map(([category, funcs]) => (
                                <optgroup key={category} label={category}>
                                    {funcs.map(func => (
                                        <option key={func.value} value={func.value}>
                                            {func.label}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                        <label className="hold-function-label" htmlFor="fs2-hold">Hold function</label>
                        <select
                            id="fs2-hold"
                            value={currentProfile.footswitches.fs2.holdFunction || ''}
                            onChange={(e) => updateFootswitch('fs2', 'holdFunction', e.target.value || null)}
                            className="config-select"
                        >
                            <option value="">None</option>
                            {Object.entries(groupedFunctions).map(([category, funcs]) => (
                                <optgroup key={category} label={category}>
                                    {funcs.map(func => (
                                        <option key={func.value} value={func.value}>
                                            {func.label}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>

                    </div>
                </div>

                {/* External Footswitches */}
                <div className="external-footswitches">
                    <h5>External Footswitch (Optional)</h5>

                    <div className="footswitch-grid">
                        <div className="footswitch-config">
                            <div className="footswitch-header">
                                <label htmlFor="ext1-function">EXT1</label>
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={currentProfile.footswitches.ext1.enabled}
                                        onChange={(e) => updateFootswitch('ext1', 'enabled', e.target.checked)}
                                    />
                                    <span>Enabled</span>
                                </label>
                            </div>
                            <select
                                id="ext1-function"
                                value={currentProfile.footswitches.ext1.function}
                                onChange={(e) => updateFootswitch('ext1', 'function', e.target.value)}
                                className="config-select"
                                disabled={!currentProfile.footswitches.ext1.enabled}
                            >
                                {Object.entries(groupedFunctions).map(([category, funcs]) => (
                                    <optgroup key={category} label={category}>
                                        {funcs.map(func => (
                                            <option key={func.value} value={func.value}>
                                                {func.label}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                            <label className="hold-function-label" htmlFor="ext1-hold">Hold function</label>
                            <select
                                id="ext1-hold"
                                value={currentProfile.footswitches.ext1.holdFunction || ''}
                                onChange={(e) => updateFootswitch('ext1', 'holdFunction', e.target.value || null)}
                                className="config-select"
                                disabled={!currentProfile.footswitches.ext1.enabled}
                            >
                                <option value="">None</option>
                                {Object.entries(groupedFunctions).map(([category, funcs]) => (
                                    <optgroup key={category} label={category}>
                                        {funcs.map(func => (
                                            <option key={func.value} value={func.value}>
                                                {func.label}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>

                        </div>

                        <div className="footswitch-config">
                            <div className="footswitch-header">
                                <label htmlFor="ext2-function">EXT2</label>
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={currentProfile.footswitches.ext2.enabled}
                                        onChange={(e) => updateFootswitch('ext2', 'enabled', e.target.checked)}
                                    />
                                    <span>Enabled</span>
                                </label>
                            </div>
                            <select
                                id="ext2-function"
                                value={currentProfile.footswitches.ext2.function}
                                onChange={(e) => updateFootswitch('ext2', 'function', e.target.value)}
                                className="config-select"
                                disabled={!currentProfile.footswitches.ext2.enabled}
                            >
                                {Object.entries(groupedFunctions).map(([category, funcs]) => (
                                    <optgroup key={category} label={category}>
                                        {funcs.map(func => (
                                            <option key={func.value} value={func.value}>
                                                {func.label}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                            <label className="hold-function-label" htmlFor="ext2-hold">Hold function</label>
                            <select
                                id="ext2-hold"
                                value={currentProfile.footswitches.ext2.holdFunction || ''}
                                onChange={(e) => updateFootswitch('ext2', 'holdFunction', e.target.value || null)}
                                className="config-select"
                                disabled={!currentProfile.footswitches.ext2.enabled}
                            >
                                <option value="">None</option>
                                {Object.entries(groupedFunctions).map(([category, funcs]) => (
                                    <optgroup key={category} label={category}>
                                        {funcs.map(func => (
                                            <option key={func.value} value={func.value}>
                                                {func.label}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>

                        </div>
                    </div>
                </div>
            </div>

            {/* Encoder Configuration */}
            <div className="config-section">
                <h4>Encoder Assignment</h4>
                <select
                    aria-label="Encoder assignment"
                    value={currentProfile.encoder.function}
                    onChange={(e) => updateEncoder(e.target.value)}
                    className="config-select"
                >
                    {ENCODER_FUNCTIONS.map(func => (
                        <option key={func.value} value={func.value}>
                            {func.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* MIDI Settings (Collapsible) */}
            {showMIDISettings && (
                <div className="config-section midi-settings animate-fadeIn">
                    <h4>MIDI Settings</h4>

                    <div className="settings-grid">
                        <div className="setting-group">
                            <label htmlFor="midi-channel">MIDI Channel</label>
                            <select
                                id="midi-channel"
                                value={currentProfile.midiSettings.channel}
                                onChange={(e) => updateMIDISetting('channel', Number(e.target.value))}
                                className="config-select"
                            >
                                {[...Array(16)].map((_, i) => (
                                    <option key={i + 1} value={i + 1}>Channel {i + 1}</option>
                                ))}
                                <option value="omni">Omni (All Channels)</option>
                            </select>
                        </div>

                        <div className="setting-group">
                            <label>MIDI Clock</label>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={currentProfile.midiSettings.clockEnabled}
                                    onChange={(e) => updateMIDISetting('clockEnabled', e.target.checked)}
                                />
                                <span>Enable External Clock Sync</span>
                            </label>
                        </div>

                        <div className="setting-group">
                            <label htmlFor="midi-input-source">Input Source</label>
                            <select
                                id="midi-input-source"
                                value={currentProfile.midiSettings.inputSource}
                                onChange={(e) => updateMIDISetting('inputSource', e.target.value)}
                                className="config-select"
                            >
                                <option value="usb">USB</option>
                                <option value="1/8in">1/8" MIDI Jack</option>
                            </select>
                        </div>

                        <div className="setting-group">
                            <label>External Control</label>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={currentProfile.midiSettings.externalControl}
                                    onChange={(e) => updateMIDISetting('externalControl', e.target.checked)}
                                />
                                <span>Enable MIDI Control</span>
                            </label>
                        </div>
                    </div>

                    {/* Track Settings */}
                    <h4>Track Settings</h4>
                    <div className="settings-grid">
                        <div className="setting-group">
                            <label htmlFor="track-mode">Loop Sync Mode</label>
                            <select
                                id="track-mode"
                                value={currentProfile.trackSettings.mode}
                                onChange={(e) => updateTrackSetting('mode', e.target.value)}
                                className="config-select"
                            >
                                <option value="serial">Serial (Independent)</option>
                                <option value="sync">Sync (Track B matches Track A)</option>
                            </select>
                        </div>

                        <div className="setting-group">
                            <label htmlFor="recording-logic">Recording Logic</label>
                            <select
                                id="recording-logic"
                                value={currentProfile.trackSettings.recordingLogic}
                                onChange={(e) => updateTrackSetting('recordingLogic', e.target.value)}
                                className="config-select"
                            >
                                <option value="auto">Auto Record</option>
                                <option value="manual">Manual Record</option>
                            </select>
                        </div>

                        <div className="setting-group">
                            <label htmlFor="overdub-mode">Overdub Mode</label>
                            <select
                                id="overdub-mode"
                                value={currentProfile.trackSettings.overdubMode}
                                onChange={(e) => updateTrackSetting('overdubMode', e.target.value)}
                                className="config-select"
                            >
                                <option value="overdub">Overdub (Layer)</option>
                                <option value="replace">Replace (Overwrite)</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* Transfer Actions */}
            <div className="config-actions">
                <button
                    className="btn-primary"
                    onClick={handleSendToDevice}
                    disabled={!isConnected}
                    title={!isConnected ? 'Connect to MIDI first' : 'Send configuration to Looper Plus'}
                >
                    <FiUpload /> Send to Device
                </button>
                <button
                    className="btn-secondary"
                    onClick={handleReadFromDevice}
                    disabled={true}
                    title="Not supported on Looper Plus (Hardware limitation)"
                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                >
                    <FiDownload /> Read from Device
                </button>
                {transferResult && (
                    <p className={`text-sm ${transferResult.success ? 'text-success' : 'text-error'}`} style={{ marginLeft: 'var(--spacing-md)' }}>
                        {transferResult.message}
                    </p>
                )}
            </div>
        </div>
    );
}

export default ConfigurationEditor;
